import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { RegularAccount } from '@/entities/account';
import { useAccountsInfo, useAccountsStore } from '@/entities/account';
import { zodResolver } from '@hookform/resolvers/zod';
import { createInternalTransferFormSchema } from './validation';
import type {
  AccountOption,
  AccountSource,
  AssetOption,
  InternalTransferFormValues,
  InternalTransferState,
} from './types';
import { useInternalTransfer, type InternalTransferResponse } from '../api';
import { useModalStore } from '@/shared/lib';

interface UseInternalTransferFormOptions {
  onValidateSuccess?: (data: InternalTransferFormValues) => void;
  openResultModal: ({
    success,
    error,
  }: {
    success: boolean;
    error?: Error;
  }) => void;
  onTransferSuccess: (data: InternalTransferResponse) => void;
}

export const useInternalTransferForm = ({
  onValidateSuccess,
  openResultModal,
  onTransferSuccess,
}: UseInternalTransferFormOptions) => {
  const { ewallet, spot, invest, leverage, earn } = useAccountsStore();
  const { accountsInfo } = useAccountsInfo();
  const { getState, setMultipleState } = useModalStore();

  const [selectedAccountFrom, setSelectedAccountFrom] = useState<
    AccountOption | undefined
  >();
  const [selectedAccountTo, setSelectedAccountTo] = useState<
    AccountOption | undefined
  >();
  const [selectedAssetFrom, setSelectedAssetFrom] = useState<
    AssetOption | undefined
  >();
  const [selectedAssetTo, setSelectedAssetTo] = useState<
    AssetOption | undefined
  >();

  const initFormData = getState('internalTransferFormValues') as
    | InternalTransferState
    | undefined;

  const accountOptions = useMemo<AccountOption[]>(() => {
    const sources: AccountSource[] = [
      {
        source: ewallet,
        type: 'ewallet',
        getAccTypeName: (accTypeName: string) => accTypeName,
      },
      {
        source: spot,
        type: 'spot',
        getAccTypeName: (accTypeName: string) => accTypeName,
      },
      {
        source: invest,
        type: 'invest',
        getAccTypeName: (accTypeName: string) => accTypeName,
      },
      {
        source: leverage,
        type: 'leverage',
        getAccTypeName: () => 'Trading',
      },
      {
        source: earn,
        type: 'earn',
        getAccTypeName: () => 'Trading',
      },
    ];

    const accounts: AccountOption[] = [];

    sources.forEach(({ source, type, getAccTypeName }) => {
      source?.accounts?.forEach(acc => {
        let tradingAccountInfo;
        if (type === 'leverage' || type === 'invest' || type === 'earn') {
          tradingAccountInfo =
            accountsInfo?.accounts.find(
              account => account.accNum === acc.accNum
            ) ?? {};
        }
        accounts.push({
          accNum: acc.accNum,
          accTypeName: getAccTypeName(
            type !== 'leverage' && type !== 'earn'
              ? (acc as RegularAccount)?.accTypeName
              : ''
          ),
          accName: tradingAccountInfo ? tradingAccountInfo?.accName : undefined,
          tradingProductCode: tradingAccountInfo
            ? tradingAccountInfo?.tradingProductCode
            : undefined,
          leverage: tradingAccountInfo
            ? String(tradingAccountInfo?.leverage?.value)
            : undefined,
          type,
        });
      });
    });

    return accounts;
  }, [ewallet, spot, invest, leverage, earn]);

  const validationSchema = useMemo(() => {
    return createInternalTransferFormSchema(
      (selectedAssetFrom?.equity ?? selectedAssetFrom?.available ?? 0) -
        (selectedAssetFrom?.margin ?? 0)
    );
  }, [selectedAssetFrom]);

  const form = useForm<InternalTransferFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(validationSchema),
    defaultValues: {
      accNumFrom: initFormData?.selectedAccountFrom?.accNum || '',
      accNumTo: initFormData?.selectedAccountTo?.accNum || '',
      assetFrom: initFormData?.selectedAssetFrom?.code || '',
      assetTo: initFormData?.selectedAssetTo?.code || '',
      amount: initFormData?.amount || undefined,
    },
  });

  const watchedValues = form.watch();

  useEffect(() => {
    if (!initFormData || !initFormData?.selectedAccountFrom) {
      return;
    }

    const {
      selectedAccountFrom,
      selectedAccountTo,
      selectedAssetFrom,
      selectedAssetTo,
      amount,
    } = initFormData;

    setSelectedAccountFrom(selectedAccountFrom);
    setSelectedAccountTo(selectedAccountTo);
    setSelectedAssetFrom(selectedAssetFrom);
    setSelectedAssetTo(selectedAssetTo);

    form.setValue('accNumFrom', selectedAccountFrom?.accNum);
    form.setValue('accNumTo', selectedAccountTo?.accNum);
    form.setValue('assetFrom', selectedAssetFrom?.code);
    form.setValue('assetTo', selectedAssetTo?.code);
    form.setValue('amount', amount);

    setMultipleState<InternalTransferState>({
      internalTransferFormValues: {
        initialFromType: selectedAccountFrom?.type,
        initialToType: selectedAccountTo?.type,
      },
    });
  }, [initFormData, form]);

  const {
    mutate: transferMutate,
    isTransferring,
    isError: isTransferError,
    transferError,
  } = useInternalTransfer({
    onSuccess: data => {
      onTransferSuccess(data);
      openResultModal({ success: true });
    },
    onError: error => {
      openResultModal({ success: false, error });
    },
  });

  const handleFormSubmit = (data: InternalTransferFormValues) => {
    if (
      onValidateSuccess &&
      (data.assetFrom !== data.assetTo ||
        selectedAccountFrom?.type === 'leverage' ||
        selectedAccountFrom?.type === 'earn')
    ) {
      setMultipleState<InternalTransferState>({
        internalTransferFormValues: {
          selectedAccountFrom,
          selectedAccountTo,
          selectedAssetFrom,
          selectedAssetTo,
          amount: data.amount,
          initialFromType: selectedAccountFrom?.type,
          initialToType: selectedAccountTo?.type,
        },
      });
      onValidateSuccess(data);
    } else {
      transferMutate({ ...data, amount: data.amount });
    }
  };

  const handleMaxClick = (amount: string) => {
    if (selectedAssetFrom) {
      form.setValue('amount', amount, {
        shouldValidate: true,
      });
    }
  };

  return {
    form,
    watchedValues,
    accountOptions,
    selectedAccountFrom,
    selectedAccountTo,
    selectedAssetFrom,
    selectedAssetTo,
    initialFromType: initFormData?.initialFromType ?? 'ewallet',
    initialToType: initFormData?.initialToType ?? 'invest',
    isInitFormData: !!initFormData,
    handleFormSubmit,
    handleMaxClick,
    setSelectedAssetFrom,
    setSelectedAssetTo,
    setSelectedAccountFrom,
    setSelectedAccountTo,
    isTransferring,
    isError: isTransferError,
    error: transferError,
  };
};
